// @ts-nocheck
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Mail, Save, Upload } from 'lucide-react';
import { TransactionTable } from '../components/TransactionTable';
import { api } from '../lib/api';
import { useYears } from '../lib/useYears';
import { formatCurrency, formatDateTime } from '../lib/utils';

export function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const clientId = Number(id);
  const { years, year: defaultYear } = useYears();
  const [client, setClient] = useState<Record<string, unknown> | null>(null);
  const [transactions, setTransactions] = useState<Record<string, unknown>[]>([]);
  const [emails, setEmails] = useState<Record<string, unknown>[]>([]);
  const [year, setYear] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [contactForm, setContactForm] = useState({ contact_name: '', contact_email: '', contact_phone: '', tax_id: '', notes: '' });
  const [selectedTxIds, setSelectedTxIds] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    if (defaultYear && !year) setYear(String(defaultYear));
  }, [defaultYear]);

  const fetchData = () => {
    if (!year) return;
    setLoading(true);
    Promise.all([
      api.clientDetail(clientId, year),
      api.clientEmails(clientId),
    ]).then(([detail, emailData]) => {
      setClient(detail.client);
      setTransactions(detail.transactions);
      setEmails(emailData.emails);
      setContactForm({
        contact_name: (detail.client.contact_name as string) || '',
        contact_email: (detail.client.contact_email as string) || '',
        contact_phone: (detail.client.contact_phone as string) || '',
        tax_id: (detail.client.tax_id as string) || '',
        notes: (detail.client.notes as string) || '',
      });
    }).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [clientId, year]);

  const handleSaveContact = async () => {
    setSaving(true);
    try {
      const res = await api.updateClient(clientId, contactForm);
      setClient(res.client);
      setEditing(false);
    } catch (err) {
      alert(String((err as Error).message));
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (txId: number, status: string, extra?: { cert_number?: string }) => {
    try {
      await api.updateTransaction(txId, { cert_status: status, ...extra });
      fetchData();
    } catch (err) {
      alert(String((err as Error).message));
    }
  };

  const handleBulkStatusChange = async (status: string) => {
    if (selectedTxIds.size === 0) return;
    try {
      await api.bulkUpdateTransactions([...selectedTxIds], status);
      setSelectedTxIds(new Set());
      fetchData();
    } catch (err) {
      alert(String((err as Error).message));
    }
  };

  const handleSendEmail = async () => {
    if (!confirm(`Send WHT certificate request email to ${client?.contact_email}?`)) return;
    setSendingEmail(true);
    try {
      await api.sendEmail(clientId, undefined, year || 'all');
      alert('Email sent successfully');
      fetchData();
    } catch (err) {
      alert(String((err as Error).message));
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading...</div>;
  if (!client) return <div className="text-center py-12 text-gray-400">Client not found</div>;

  const missingTx = transactions.filter(t => ['missing', 'requested'].includes(t.cert_status as string));
  const totalAmount = transactions.reduce((s, t) => s + Number(t.amount), 0);
  const missingAmount = missingTx.reduce((s, t) => s + Number(t.amount), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/wht/clients" className="text-gray-400 hover:text-gray-600"><ArrowLeft size={20} /></Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{client.name as string}</h1>
          <p className="text-sm text-gray-500">
            {transactions.length} transactions &middot; {formatCurrency(totalAmount)} total &middot;{' '}
            <span className="text-red-600">{formatCurrency(missingAmount)} missing</span>
          </p>
        </div>
        <select value={year} onChange={e => setYear(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          <option value="all">All years</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {/* Contact Info */}
      <div className="bg-white rounded-lg border p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">Contact Information</h3>
          <div className="flex gap-2">
            {!editing ? (
              <button onClick={() => setEditing(true)} className="text-sm text-blue-600 hover:underline">Edit</button>
            ) : (
              <>
                <button onClick={() => setEditing(false)} className="text-sm text-gray-500 hover:underline">Cancel</button>
                <button onClick={handleSaveContact} disabled={saving} className="flex items-center gap-1 text-sm text-blue-600 hover:underline">
                  <Save size={14} /> Save
                </button>
              </>
            )}
          </div>
        </div>
        {editing ? (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-500">Contact Name</label>
              <input value={contactForm.contact_name} onChange={e => setContactForm(f => ({ ...f, contact_name: e.target.value }))} className="w-full border rounded px-3 py-2 text-sm mt-1" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Email</label>
              <input value={contactForm.contact_email} onChange={e => setContactForm(f => ({ ...f, contact_email: e.target.value }))} className="w-full border rounded px-3 py-2 text-sm mt-1" type="email" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Phone</label>
              <input value={contactForm.contact_phone} onChange={e => setContactForm(f => ({ ...f, contact_phone: e.target.value }))} className="w-full border rounded px-3 py-2 text-sm mt-1" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Tax ID</label>
              <input value={contactForm.tax_id} onChange={e => setContactForm(f => ({ ...f, tax_id: e.target.value }))} className="w-full border rounded px-3 py-2 text-sm mt-1" />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-gray-500">Notes</label>
              <textarea value={contactForm.notes} onChange={e => setContactForm(f => ({ ...f, notes: e.target.value }))} className="w-full border rounded px-3 py-2 text-sm mt-1" rows={2} />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="text-gray-500">Contact:</span> {(client.contact_name as string) || '-'}</div>
            <div><span className="text-gray-500">Email:</span> {(client.contact_email as string) || <span className="text-red-400">Not set</span>}</div>
            <div><span className="text-gray-500">Phone:</span> {(client.contact_phone as string) || '-'}</div>
            <div><span className="text-gray-500">Tax ID:</span> {(client.tax_id as string) || '-'}</div>
            {client.notes && <div className="col-span-2"><span className="text-gray-500">Notes:</span> {client.notes as string}</div>}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3 flex-wrap">
        {client.contact_email && missingTx.length > 0 && (
          <button
            onClick={handleSendEmail}
            disabled={sendingEmail}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            <Mail size={14} /> {sendingEmail ? 'Sending...' : `Request Certificates (${missingTx.length} missing)`}
          </button>
        )}
        {selectedTxIds.size > 0 && (
          <>
            <button onClick={() => handleBulkStatusChange('received')} className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm hover:bg-blue-200">
              Mark {selectedTxIds.size} as Received
            </button>
            <button onClick={() => handleBulkStatusChange('missing')} className="px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm hover:bg-red-200">
              Reset {selectedTxIds.size} to Missing
            </button>
            <button onClick={() => handleBulkStatusChange('verified')} className="px-4 py-2 bg-green-100 text-green-700 rounded-lg text-sm hover:bg-green-200">
              Mark {selectedTxIds.size} as Verified
            </button>
            <button onClick={() => handleBulkStatusChange('waived')} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200">
              Waive {selectedTxIds.size}
            </button>
          </>
        )}
      </div>

      {/* Transactions */}
      <div className="bg-white rounded-lg border">
        <div className="p-4 border-b">
          <h3 className="font-semibold">Transactions ({transactions.length})</h3>
        </div>
        <TransactionTable
          transactions={transactions as Parameters<typeof TransactionTable>[0]['transactions']}
          selectable
          selectedIds={selectedTxIds}
          onSelectionChange={setSelectedTxIds}
          onStatusChange={handleStatusChange}
        />
      </div>

      {/* Email History */}
      <div className="bg-white rounded-lg border">
        <div className="p-4 border-b">
          <h3 className="font-semibold">Email History</h3>
        </div>
        <div className="divide-y">
          {emails.map((e) => (
            <div key={e.id as number} className="px-4 py-3 text-sm">
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium">{e.subject as string}</span>
                  <span className="text-gray-400 mx-2">&rarr;</span>
                  <span className="text-gray-600">{e.sent_to as string}</span>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded ${e.status === 'sent' ? 'bg-green-100 text-green-700' : e.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                  {e.status as string}
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-1">{formatDateTime(e.created_at as string)}</p>
            </div>
          ))}
          {emails.length === 0 && (
            <p className="px-4 py-6 text-sm text-gray-400 text-center">No emails sent to this client</p>
          )}
        </div>
      </div>
    </div>
  );
}
